import { useForm } from "../../../hooks/useForm";
import {
  CreateBannerAsAdminDto,
  useCreateBannerAsAdminMutation,
  useGetBannerAsAdminQuery,
  useUpdateBannerAsAdminMutation,
} from "../../../lib/apollo/graphql/generated";
import { formatDate } from "../../../utilities/format";
import ActiveStatusSelectBox from "../../common/components/ActiveStatusSelectBox";
import BasicButton from "../../common/components/BasicButton";
import Input from "../../common/components/Input";
import LabeledInput from "../../common/components/LabeledInput";
import Modal, { ModalProps } from "../../common/components/Modal";
import LinkTypeInput from "./LinkTypeSelectBox";

interface UpdateBannerModalProps extends ModalProps {
  bannerId: string | null;
}

type UpdateBannerForm = {
  id: string;
  title: string;
  description: string;
  linkTypeId: string;
  linkUrl: string;
  endAt: string;
  startAt: string;
  statusId: string;
  thumbnailMediaInput: {
    file: File | null;
  };
};

export default function UpdateBannerModal({
  open,
  onClose,
  bannerId,
}: UpdateBannerModalProps) {
  const [form, onChange, onBatch] = useForm<UpdateBannerForm>({
    id: bannerId || "",
    title: "",
    description: "",
    linkTypeId: "",
    linkUrl: "",
    endAt: "",
    startAt: "",
    statusId: "",
    thumbnailMediaInput: {
      file: null,
    },
  });

  const onMediaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    onChange("thumbnailMediaInput", { file });
  };

  const isCreate = !bannerId;

  const { loading: isInitializing } = useGetBannerAsAdminQuery({
    skip: isCreate,
    variables: {
      bannerId: bannerId!,
    },
    onCompleted: (res) => {
      const banner = res.getBannerAsAdmin;

      onBatch({
        id: banner.id,
        title: banner.title,
        description: banner.description || "",
        linkTypeId: banner.linkTypeId || "",
        linkUrl: banner.linkUrl,
        endAt: formatDate(banner.endAt, "YYYY-MM-DD"),
        startAt: formatDate(banner.startAt, "YYYY-MM-DD"),
        statusId: banner.statusId || "",
        thumbnailMediaInput: {
          file: null,
        },
      });
    },
  });

  const [createBanner, { loading: isCreating }] =
    useCreateBannerAsAdminMutation({
      onCompleted: () => {
        window.alert("배너를 등록했습니다.");
        onClose();
      },
      refetchQueries: ["GetBannersAsAdmin", "GetBannerCountsAsAdmin"],
    });

  const [updateBanner, { loading: isUpdating }] =
    useUpdateBannerAsAdminMutation({
      onCompleted: () => {
        window.alert("배너를 수정했습니다.");
        onClose();
      },
      refetchQueries: ["GetBannersAsAdmin", "GetBannerCountsAsAdmin"],
    });

  const isSubmitDisabled =
    isUpdating ||
    isCreating ||
    !form.description ||
    !form.title ||
    !form.linkTypeId ||
    !form.startAt ||
    !form.endAt ||
    !form.statusId ||
    (isCreate && !form.thumbnailMediaInput.file);

  const onExposurePeriodChange = (key: "startAt" | "endAt", value: string) => {
    const { startAt, endAt } = form;

    const selected = new Date(value).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);

    if (key === "startAt" && endAt && selected > today) {
      window.alert("종료일 이후 시작할 수 없습니다.");
      return;
    }

    if (key === "startAt" && selected < today) {
      window.alert("시작일은 오늘 이후 날짜로만 설정 가능합니다.");
      return;
    }

    if (key === "endAt" && startAt && selected < today) {
      window.alert("시작일 이전 종료할 수 없습니다.");
      return;
    }

    onChange(key, value);
  };

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    const isFileAttached = !!form.thumbnailMediaInput.file;

    const sharedVariables: CreateBannerAsAdminDto = {
      description: form.description,
      endAt: form.endAt,
      linkTypeId: form.linkTypeId,
      linkUrl: form.linkUrl,
      startAt: form.startAt,
      statusId: form.statusId,
      title: form.title,
      thumbnailMediaInput: isFileAttached
        ? form.thumbnailMediaInput
        : undefined,
    };

    if (isCreate) {
      createBanner({
        variables: {
          createBannerAsAdminDto: sharedVariables,
        },
      });
    } else {
      updateBanner({
        variables: {
          updateBannerAsAdminDto: {
            ...sharedVariables,
            id: form.id,
          },
        },
      });
    }
  };

  return (
    <Modal
      title={`배너 ${isCreate ? "등록" : "수정"}`}
      open={open}
      onClose={onClose}
      height="900px"
      isLoading={isInitializing}
    >
      <form
        onSubmit={onSubmit}
        className="flex h-full max-h-full flex-col gap-5 overflow-y-auto"
      >
        <LabeledInput
          label="배너명"
          value={form.title}
          onValueChange={(value) => onChange("title", value)}
        />
        <LabeledInput
          label="설명"
          value={form.description}
          onValueChange={(value) => onChange("description", value)}
        />
        <LabeledInput label="썸네일" type="file" onChange={onMediaChange} />
        <div className="flex flex-col gap-1">
          <p className="text-lg font-bold">노출 기간</p>
          <div className="flex gap-3">
            <Input
              value={form.startAt}
              type="date"
              onValueChange={(value) => {
                onExposurePeriodChange("startAt", value);
              }}
            />
            <Input
              value={form.endAt}
              type="date"
              onValueChange={(value) => {
                onExposurePeriodChange("endAt", value);
              }}
            />
          </div>
        </div>
        <LinkTypeInput
          link={form.linkUrl}
          typeId={form.linkTypeId}
          onTypeSelect={(typeId) => onChange("linkTypeId", typeId)}
          onUrlChange={(url) => onChange("linkUrl", url)}
        />
        <ActiveStatusSelectBox
          value={form.statusId}
          onSelect={(value) => onChange("statusId", value)}
        />
        <BasicButton
          text={isCreate ? "등록" : "수정"}
          type="submit"
          loading={isUpdating || isCreating}
          disabled={isSubmitDisabled}
          className="mt-auto w-1/2 self-center"
        />
      </form>
    </Modal>
  );
}
